'use client';

import { createContext, useContext, ReactNode } from 'react';

// Widget configuration types
interface WidgetConfig {
  enabled: boolean;
  excludePaths?: string[];
  includePaths?: string[];
}

interface WidgetSettings {
  socialShare: WidgetConfig;
  socialProof: WidgetConfig;
  exitIntent: WidgetConfig;
}

// Default configuration - EASY TO MODIFY
const defaultWidgetSettings: WidgetSettings = {
  socialShare: {
    enabled: true,
    // Option 1: Exclude specific pages (easier)
    excludePaths: [
      '/admin',
      '/dashboard', 
      '/checkout',
      '/payment'
    ]
    // Option 2: Include only specific pages (uncomment if preferred)
    // includePaths: ['/blog', '/mortgage-marketing', '/resources']
  },
  socialProof: {
    enabled: true, // Live social proof popups
    excludePaths: [
      '/admin', 
      '/dashboard',
      '/checkout',
      '/payment',
      '/login',
      // privacy pages (owner 2026-10-14)
      '/privacy-policy',
      '/privacy-choices',
      '/ccpa-compliance',
      '/we-do-not-sell-personal-information',
      '/disclosure-and-deletion-requests'
    ]
  },
  exitIntent: {
    enabled: true, // Exit intent popup
    excludePaths: [
      '/mortgage-lead-generation-demo',  // Demo page gets special treatment
      '/schedule-demo',                  // Other demo variations
      '/thank-you',                       // Don't annoy on thank you pages
      '/checkout',                        // Don't interrupt checkout
      '/payment',                         // Don't interrupt payment
      '/confirmation'                     // Don't show on confirmations
    ]
  }
};

// Context for widgets
const WidgetContext = createContext<WidgetSettings>(defaultWidgetSettings);

// Hook to use widget settings
export const useWidgetSettings = () => useContext(WidgetContext);

// Check if widget should be shown on current path
export const useWidgetVisibility = (widgetType: keyof WidgetSettings) => {
  const settings = useWidgetSettings();
  const config = settings[widgetType];
  
  // Get current path (client-side only)
  if (typeof window === 'undefined') return false;
  
  const currentPath = window.location.pathname;
  
  if (!config.enabled) return false;
  
  // If using excludePaths (recommended approach)
  if (config.excludePaths?.length) {
    return !config.excludePaths.some(path => 
      currentPath.startsWith(path)
    );
  }
  
  // If using includePaths
  if (config.includePaths?.length) {
    return config.includePaths.some(path => 
      path === '/' ? currentPath === '/' : currentPath.startsWith(path)
    );
  }
  
  // Default: show everywhere if enabled
  return true;
};

// Provider component
interface WidgetProviderProps {
  children: ReactNode;
  settings?: Partial<WidgetSettings>;
}

export const WidgetProvider = ({ 
  children, 
  settings = {} 
}: WidgetProviderProps) => {
  const mergedSettings = {
    ...defaultWidgetSettings,
    ...settings,
    socialShare: {
      ...defaultWidgetSettings.socialShare,
      ...settings.socialShare
    },
    socialProof: {
      ...defaultWidgetSettings.socialProof,
      ...settings.socialProof
    },
    exitIntent: {
      ...defaultWidgetSettings.exitIntent,
      ...settings.exitIntent
    }
  };
  
  return (
    <WidgetContext.Provider value={mergedSettings}>
      {children}
    </WidgetContext.Provider>
  );
};